import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Spin,
  Alert,
  Card,
  Space,
  Input,
  Switch,
  Checkbox,
  Radio,
  Select,
  InputNumber,
  FloatButton,
  message,
  Button
} from "antd";
import { ShareAltOutlined } from "@ant-design/icons";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../FirebaseConfig";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import * as htmlToImage from "html-to-image";
import { Popconfirm } from "antd";

dayjs.extend(utc);
dayjs.extend(timezone);

const { TextArea } = Input;
const { Option } = Select;

const AppViewPage = () => {
  const { workspaceId, appId } = useParams();
  const containerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [alertConfig, setAlertConfig] = useState(null);
  const [appData, setAppData] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [values, setValues] = useState({});

  const storageKey = `divehrzn_${workspaceId}_${appId}`;

  const workspaceDocRef = doc(
    db,
    "dive_hrzn_apps",
    workspaceId,
    "workspace",
    "accountDetails",
  );

  const appDocRef = doc(db, "dive_hrzn_apps", workspaceId, "apps", appId);

  /* ---------------- LOAD DATA ---------------- */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        // Workspace check
        const wsSnap = await getDoc(workspaceDocRef);
        if (!wsSnap.exists()) {
          block("Workspace not found", "Please contact DiveHRZN support");
          return;
        }

        const ws = wsSnap.data();
        setWorkspace(ws);

        if (!ws.activeStatus) {
          block("Workspace inactive", "Please contact DiveHRZN team");
          return;
        }

        const expiry = dayjs(ws.planExpireDate.toDate());
        const daysLeft = expiry.diff(dayjs(), "day");

        if (daysLeft < 0) {
          block("Subscription expired", "Contact DiveHRZN team to renew");
          return;
        }

        let type = "info";
        if (daysLeft <= 5) type = "error";
        else if (daysLeft <= 10) type = "warning";

        setAlertConfig({
          type,
          message: "Subscription active",
          description: `Expires in ${daysLeft} day${daysLeft > 1 ? "s" : ""}`,
        });

        // App data
        const appSnap = await getDoc(appDocRef);
        if (!appSnap.exists()) {
          block("App not available", "Contact admin");
          return;
        }

        const app = appSnap.data();
        if (!app.enabled) {
          block("App disabled", "Contact admin");
          return;
        }

        setAppData(app);

        // Load saved values
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          setValues(JSON.parse(stored));
        }
      } catch (err) {
        console.error(err);
        message.error("Failed to load app");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [workspaceId, appId]);

  const block = (msg, desc) => {
    setBlocked(true);
    setAlertConfig({
      type: "error",
      message: msg,
      description: desc,
    });
  };
const handleReset = () => {
  localStorage.removeItem(storageKey);
  setValues({});
  message.success("All inputs reset");
};

  /* ---------------- SAVE FIELD VALUE ---------------- */
  const updateValue = (key, value) => {
    const updated = { ...values, [key]: value };
    setValues(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  /* ---------------- SHARE / DOWNLOAD ---------------- */
  const handleShare = async () => {
    try {
      const blob = await htmlToImage.toBlob(containerRef.current);
      const file = new File([blob], `${appId}.png`, {
        type: "image/png",
      });

      // Native share (HTTPS + supported devices)
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          files: [file],
          title: `${workspaceId}-${appId}`,
        });
        return;
      }

      // Fallback → auto download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${appId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      message.success("Image saved to gallery. Share via WhatsApp 📤");
    } catch (err) {
      console.error(err);
      message.error("Unable to share image");
    }
  };

  /* ---------------- LOADING ---------------- */
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  /* ---------------- BLOCKED ---------------- */
  if (blocked) {
    return (
      <div style={{ padding: 16 }}>
        <Alert {...alertConfig} showIcon />
      </div>
    );
  }

  /* ---------------- FIELD RENDER ---------------- */
  const renderField = (field) => {
    const key = field.label;

    switch (field.type) {
      case "switch":
        return (
          <Switch checked={values[key]} onChange={(v) => updateValue(key, v)} />
        );

      case "checkbox":
        return (
          <Checkbox
            checked={values[key]}
            onChange={(e) => updateValue(key, e.target.checked)}
          >
            {field.label}
          </Checkbox>
        );

      case "radio":
        return (
          <Radio.Group
            value={values[key]}
            onChange={(e) => updateValue(key, e.target.value)}
          >
            {field.options?.map((o) => (
              <Radio key={o} value={o}>
                {o}
              </Radio>
            ))}
          </Radio.Group>
        );

      case "input":
        return (
          <Input
            value={values[key]}
            onChange={(e) => updateValue(key, e.target.value)}
          />
        );

      case "textarea":
        return (
          <TextArea
            rows={3}
            value={values[key]}
            onChange={(e) => updateValue(key, e.target.value)}
          />
        );

      case "dropdown":
        return (
          <Select
            value={values[key]}
            onChange={(v) => updateValue(key, v)}
            style={{ width: "100%" }}
          >
            {field.options?.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
        );

      case "multiselect":
        return (
          <Select
            mode="multiple"
            value={values[key]}
            onChange={(v) => updateValue(key, v)}
            style={{ width: "100%" }}
          >
            {field.options?.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
        );

      case "number":
        return (
          <InputNumber
            value={values[key]}
            onChange={(v) => updateValue(key, v)}
            style={{ width: "100%" }}
          />
        );

      default:
        return null;
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      {alertConfig && (
        <Alert
          type={alertConfig.type}
          message={alertConfig.description}
          showIcon
          closable={false}
          style={{
            marginBottom: 16,
            fontSize: 12,
            padding: "6px 12px",
          }}
        />
      )}
      <div ref={containerRef} style={{ padding: 16 }}>
        {/* Header (included in image) */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <Card
            bordered
            style={{
              textAlign: "center",
              marginBottom: 16,
              background: "#fff",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              {workspaceId.toUpperCase()}
            </div>

            {/* <div style={{ fontSize: 12, color: "#666" }}>
    {workspace?.location || "Vizag"}
  </div> */}

            <div style={{ fontSize: 12, marginTop: 4 }}>
              {dayjs().tz("Asia/Kolkata").format("DD MMM YYYY · hh:mm A")} IST
            </div>
          </Card>
        </div>

        {/* Subscription info (small & clean) */}

        {/* App content */}
        {appData.sections.map((section, i) => (
          <Card key={i} title={section.title} style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {section.fields
                .filter((f) => f.enabled)
                .map((field, idx) => (
                  <div key={idx}>
                    {field.type !== "checkbox" && (
                      <div style={{ marginBottom: 4 }}>{field.label}</div>
                    )}
                    {renderField(field)}
                  </div>
                ))}
            </Space>
          </Card>
        ))}
      </div>
<div
  style={{
    position: "fixed",
    bottom: 90,
    right: 16,
    zIndex: 1000,
  }}
>
  <Popconfirm
    title="Reset all inputs?"
    description="This will clear all entered data. This action cannot be undone."
    okText="Reset"
    cancelText="Cancel"
    onConfirm={handleReset}
  >
    <Button danger size="small">
      Reset
    </Button>
  </Popconfirm>
</div>

      {/* Floating Share Button */}
      <FloatButton icon={<ShareAltOutlined />} onClick={handleShare} />
    </>
  );
};

export default AppViewPage;
